import MaterialsOverview from './overview/MaterialsOverview';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useBillableMaterialsController } from './useBillableMaterialsController';
import { UserPopulatedDTO } from '../../api/generated';
import MaterialEditor from './editor/MaterialEditor';
import { useTranslation } from 'react-i18next';
import CategoryIcon from '@mui/icons-material/Category';

interface BillableMaterialsProps {
  user: UserPopulatedDTO;
}

export default function BillableMaterials({ user }: BillableMaterialsProps) {
  const { t } = useTranslation();
  const organisationId = user.organisation.id;

  const {
    materials,
    deleteMaterial,
    saveMaterialIfDirty,
    selectMaterial,
    addNewMaterial,
    activeMaterial,
    updateActiveItem,
    dirtyMaterialId,
    displayMaterials,
  } = useBillableMaterialsController(organisationId);

  const handleDuplicateMaterial = (id: string) => {
    const duplicate = materials?.find((m) => m.id === id);
    if (duplicate) {
      addNewMaterial(duplicate);
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: '1em' }}>
      <MaterialsOverview
        materials={displayMaterials}
        selectedMaterialId={activeMaterial?.id}
        dirtyMaterialId={dirtyMaterialId}
        onSelectMaterial={selectMaterial}
        onAddNewMaterial={addNewMaterial}
        onDuplicateMaterial={handleDuplicateMaterial}
        onDeleteMaterial={deleteMaterial}
      />

      {activeMaterial ? (
        <MaterialEditor
          jobsites={user.jobsites}
          material={activeMaterial}
          onDelete={deleteMaterial}
          onChange={updateActiveItem}
          onSave={saveMaterialIfDirty}
          isDirty={!dirtyMaterialId}
        />
      ) : (
        <Paper
          elevation={8}
          sx={{
            flex: 3,
            borderRadius: '10px',
            transition: 'all 0.4s ease-in-out',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            overflow: 'hidden',
            padding: '1em',
          }}
        >
          {!materials?.length ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1em',
              }}
            >
              <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
              <Typography variant='h6' sx={{ color: 'text.secondary' }}>
                {t('billableMaterial.noneConfigured')}
              </Typography>
              <Button onClick={() => addNewMaterial()} variant='contained'>
                {t('billableMaterial.add')}
              </Button>
            </Box>
          ) : (
            <Typography variant='h6'>{t('billableMaterial.selectMaterial')}</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
